import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../utils/axiosInstance";
import {
  Container, Card, Row, Col, Table, Button, Form, Modal, Badge,
  Alert, Spinner, InputGroup, Pagination, OverlayTrigger, Tooltip,
  ProgressBar, Nav, Tab, ListGroup, ButtonGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = "/abakobwa_babyaye";

function AbakobwaBabyaye() {
  // ══════════════════════════════════════════════════════════════
  // STATE MANAGEMENT
  // ══════════════════════════════════════════════════════════════
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // CRUD State
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSchool, setFilterSchool] = useState("all");
  const [filterSupport, setFilterSupport] = useState("all");
  const [filterAge, setFilterAge] = useState("all");
  
  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [activeTab, setActiveTab] = useState("data");
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Form State
  const [errors, setErrors] = useState({});
  const [autoFillKey, setAutoFillKey] = useState(0);

  const itemsPerPage = 8;

  const emptyForm = {
    amazina: "",
    imyaka: "",
    aderesi: "",
    nimero_ya_telephone: "",
    umwana_ufite_imyaka: "",
    asubiye_mw_ishuri: "Oya",
    ubufasha_yahawe: "",
    icyo_akeneye: "",
    ishuri_ryiga: "",
    umwaka_w_amashuri: "",
    ababyeyi_bazi: "Yego",
    afite_ubufasha: "Oya",
  };

  const [form, setForm] = useState({ ...emptyForm });

  // ══════════════════════════════════════════════════════════════
  // THEME CONFIGURATION
  // ══════════════════════════════════════════════════════════════
  const theme = {
    bg: "#0a0a0f",
    surface: "rgba(17, 17, 27, 0.92)",
    surfaceHover: "rgba(30, 30, 45, 0.95)",
    text: "#f0f0f5",
    textMuted: "#8b8b9e",
    border: "rgba(255, 255, 255, 0.08)",
    blur: "blur(24px)",
    primary: "#00d4ff",
    primaryDark: "#0099cc",
    primaryGlow: "rgba(0, 212, 255, 0.25)",
    secondary: "#7c3aed",
    danger: "#ff4757",
    dangerGlow: "rgba(255, 71, 87, 0.2)",
    warning: "#ffa502",
    success: "#2ed573",
    successGlow: "rgba(46, 213, 115, 0.2)",
    gradient: "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
    shadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    shadowHover: "0 16px 48px rgba(0, 212, 255, 0.15)",
  };

  // ══════════════════════════════════════════════════════════════
  // DATA GENERATORS
  // ══════════════════════════════════════════════════════════════
  const generateRandomData = useCallback(() => {
    const firstNames = [
      "Mukamana", "Uwimana", "Muhire", "Ingabire", "Uwase", "Mukeshimana",
      "Nyiraneza", "Umutoni", "Ishimwe", "Murekatete", "Uwamahoro", "Niyonzima",
      "Mukandayisenga", "Nyirahabimana", "Uwamariya", "Kamanzi", "Niyigena",
      "Uwingabire", "Nizeyimana", "Mukagatare", "Nyiramana", "Uwimbabazi",
      "Mushimiyimana", "Dusabimana", "Kangabe", "Mukankusi", "Uwera",
      "Iradukunda", "Mutesi", "Kayitesi", "Ineza", "Umuhoza", "Izabayo",
    ];

    const lastNames = [
      "Alice", "Diane", "Claudine", "Marie", "Jeannette", "Grace", "Vestine",
      "Chantal", "Sandrine", "Ange", "Brigitte", "Consolée", "Odette",
      "Immaculée", "Esperance", "Justine", "Alphonsine", "Dancilla", "Anita",
      "Béatrice", "Deborah", "Esther", "Florence", "Henriette", "Josiane",
    ];

    const districts = [
      "Gasabo", "Kicukiro", "Nyarugenge", "Huye", "Musanze", "Rubavu",
      "Nyagatare", "Rusizi", "Karongi", "Rwamagana", "Muhanga", "Kamonyi",
      "Bugesera", "Gatsibo", "Kayonza", "Ngoma", "Nyamasheke", "Rutsiro",
    ];

    const sectors = [
      "Kimironko", "Gisozi", "Kacyiru", "Remera", "Niboye", "Gahanga",
      "Tumba", "Muhoza", "Gisenyi", "Kamembe", "Bwishyura", "Gikondo",
      "Nyamirambo", "Jabana", "Rusororo", "Masaka", "Kanombe", "Kibagabaga",
    ];

    const schools = [
      "GS Kigali", "Lycée de Kigali", "FAWE Girls School", "GS Kimisagara",
      "College Saint André", "ES Musanze", "GS Rubavu", "ES Huye",
      "GS Nyagatare", "College APRED Kagarama", "ES Officielle de Butare",
      "GS Remera Catholique", "Lycée Notre Dame de Cîteaux",
    ];

    const needs = [
      "Amafaranga y'ishuri", "Ibikoresho by'ishuri", "Ubuvuzi bwe n'ubw'umwana",
      "Inzu yo guturamo", "Ibiribwa by'umwana", "Ubufasha bw'imitekerereze",
      "Amahugurwa y'imyuga", "Impuzandengo y'ubuzima", "Ubufasha bwo kwiga umwuga",
    ];

    const supports = [
      "Ubufasha bw'amafaranga y'ishuri", "Ibikoresho by'ishuri",
      "Ubuvuzi ku buntu", "Amahugurwa y'imyuga", "Ubujyanama bw'imitekerereze",
    ];

    const schoolYears = ["P4", "P5", "P6", "S1", "S2", "S3", "S4", "S5", "S6"];

    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const motherAge = randomInt(14, 23);
    const childAge = randomInt(0, Math.min(5, motherAge - 13));
    const phonePrefix = ["078", "072", "073", "079"];
    const phone = pick(phonePrefix) + Math.floor(Math.random() * 10000000).toString().padStart(7, "0");

    const inSchool = Math.random() > 0.45 ? "Yego" : "Oya";
    const hasSupport = Math.random() > 0.55 ? "Yego" : "Oya";
    const parentsKnow = Math.random() > 0.25 ? "Yego" : "Oya";

    return {
      amazina: `${pick(firstNames)} ${pick(lastNames)}`,
      imyaka: motherAge.toString(),
      aderesi: `${pick(districts)}, ${pick(sectors)}`,
      nimero_ya_telephone: phone,
      umwana_ufite_imyaka: childAge.toString(),
      asubiye_mw_ishuri: inSchool,
      ubufasha_yahawe: hasSupport === "Yego" ? pick(supports) : "",
      icyo_akeneye: pick(needs),
      ishuri_ryiga: inSchool === "Yego" ? pick(schools) : "",
      umwaka_w_amashuri: inSchool === "Yego" ? pick(schoolYears) : "",
      ababyeyi_bazi: parentsKnow,
      afite_ubufasha: hasSupport,
    };
  }, []);

  const getUniqueRandomData = useCallback(() => {
    const existingPhones = new Set(data.map((item) => item.nimero_ya_telephone));
    let candidate;
    let attempts = 0;

    do {
      candidate = generateRandomData();
      attempts++;
    } while (existingPhones.has(candidate.nimero_ya_telephone) && attempts < 50);

    return candidate;
  }, [data, generateRandomData]);

  // ══════════════════════════════════════════════════════════════
  // STATISTICS CALCULATION
  // ══════════════════════════════════════════════════════════════
  const statistics = useMemo(() => {
    if (!data.length) return {
      total: 0, inSchool: 0, outOfSchool: 0, under16: 0,
      age16to18: 0, above18: 0, avgMotherAge: 0, avgChildAge: 0,
      needsUrgentSupport: 0, withSupport: 0,
    };

    return {
      total: data.length,
      inSchool: data.filter(d => d.asubiye_mw_ishuri === "Yego").length,
      outOfSchool: data.filter(d => d.asubiye_mw_ishuri === "Oya").length,
      under16: data.filter(d => parseInt(d.imyaka) < 16).length,
      age16to18: data.filter(d => parseInt(d.imyaka) >= 16 && parseInt(d.imyaka) <= 18).length,
      above18: data.filter(d => parseInt(d.imyaka) > 18).length,
      avgMotherAge: Math.round(data.reduce((s, d) => s + parseInt(d.imyaka || 0), 0) / data.length),
      avgChildAge: Math.round(data.reduce((s, d) => s + parseInt(d.umwana_ufite_imyaka || 0), 0) / data.length),
      needsUrgentSupport: data.filter(d => parseInt(d.imyaka) < 16 && d.asubiye_mw_ishuri === "Oya" && d.afite_ubufasha !== "Yego").length,
      withSupport: data.filter(d => d.afite_ubufasha === "Yego").length,
    };
  }, [data]);

  // ══════════════════════════════════════════════════════════════
  // DATA LOADING
  // ══════════════════════════════════════════════════════════════
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const raw = res.data;
      setData(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    } catch {
      showAlert("Habaye ikosa mu gushaka amakuru", "danger");
    }
    setLoading(false);
  };

  // ══════════════════════════════════════════════════════════════
  // FILTERING & PAGINATION
  // ══════════════════════════════════════════════════════════════
  const filtered = useMemo(() => {
    let result = [...data];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.amazina?.toLowerCase().includes(term) ||
        item.aderesi?.toLowerCase().includes(term) ||
        item.nimero_ya_telephone?.includes(term)
      );
    }

    if (filterSchool !== "all") {
      result = result.filter(item => item.asubiye_mw_ishuri === filterSchool);
    }

    if (filterSupport !== "all") {
      result = result.filter(item => item.afite_ubufasha === filterSupport);
    }

    if (filterAge !== "all") {
      result = result.filter(item => {
        const age = parseInt(item.imyaka);
        if (filterAge === "under16") return age < 16;
        if (filterAge === "16-18") return age >= 16 && age <= 18;
        if (filterAge === "above18") return age > 18;
        return true;
      });
    }

    return result;
  }, [data, searchTerm, filterSchool, filterSupport, filterAge]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterSchool, filterSupport, filterAge]);

  // ══════════════════════════════════════════════════════════════
  // PRIORITY LOGIC
  // ══════════════════════════════════════════════════════════════
  const getPriorityLevel = (age, inSchool, hasSupport) => {
    const ageNum = parseInt(age);
    if (ageNum < 16 && inSchool === "Oya" && hasSupport === "Oya") return "critical";
    if (ageNum <= 18 && inSchool === "Oya") return "high";
    if (inSchool === "Oya" || hasSupport === "Oya") return "medium";
    return "low";
  };

  const priorityConfig = {
    critical: { color: theme.danger, icon: "exclamation-triangle-fill", label: "Byihutirwa Cyane", animate: true },
    high: { color: theme.warning, icon: "exclamation-circle-fill", label: "Byihutirwa" },
    medium: { color: theme.primary, icon: "info-circle-fill", label: "Bisanzwe" },
    low: { color: theme.success, icon: "check-circle-fill", label: "Byiza" },
  };

  // ══════════════════════════════════════════════════════════════
  // ALERT & VALIDATION
  // ══════════════════════════════════════════════════════════════
  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 4500);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.amazina.trim()) newErrors.amazina = "Amazina arakenewe";
    if (!form.imyaka || parseInt(form.imyaka) < 10 || parseInt(form.imyaka) > 25) {
      newErrors.imyaka = "Imyaka (10-25)";
    }
    if (!form.aderesi.trim()) newErrors.aderesi = "Aderesi irakenewe";
    if (!form.nimero_ya_telephone.trim()) {
      newErrors.nimero_ya_telephone = "Telephone irakenewe";
    } else if (!/^\d{10}$/.test(form.nimero_ya_telephone.replace(/\s/g, ""))) {
      newErrors.nimero_ya_telephone = "Imibare 10 gusa";
    }

    // Check for duplicate phone
    if (!editId && form.nimero_ya_telephone) {
      const exists = data.some(item => item.nimero_ya_telephone === form.nimero_ya_telephone);
      if (exists) newErrors.nimero_ya_telephone = "Numero isanzwe!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ══════════════════════════════════════════════════════════════
  // CRUD OPERATIONS
  // ══════════════════════════════════════════════════════════════
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, form);
        showAlert("Byahinduwe neza!", "success");
      } else {
        await axios.post(API_URL, form);
        showAlert("Byongewe neza!", "success");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleInstantSave = async () => {
    const generated = getUniqueRandomData();
    setSaving(true);
    try {
      await axios.post(API_URL, generated);
      showAlert(`"${generated.amazina}" yongewe!`, "success");
      loadData();
    } catch {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleBatchSave = async (count = 5) => {
    setSaving(true);
    let success = 0;

    for (let i = 0; i < count; i++) {
      const generated = getUniqueRandomData();
      try {
        await axios.post(API_URL, generated);
        data.push(generated);
        success++;
      } catch {}
    }

    if (success > 0) {
      showAlert(`${success} yongewe neza!`, "success");
      loadData();
    } else {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/${deleteId}`);
      showAlert("Byasibwe neza!", "success");
      loadData();
    } catch {
      showAlert("Gusiba byananiranye", "danger");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleEdit = (item) => {
    setForm({ ...item });
    setEditId(item.id);
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setErrors({});
  };

  const openNewForm = () => {
    const generated = getUniqueRandomData();
    setForm(generated);
    setEditId(null);
    setErrors({});
    setAutoFillKey(k => k + 1);
    setShowModal(true);
  };

  const regenerateForm = () => {
    const generated = getUniqueRandomData();
    setForm(generated);
    setErrors({});
    setAutoFillKey(k => k + 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  // ══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ══════════════════════════════════════════════════════════════
  const PriorityBadge = ({ age, inSchool, hasSupport }) => {
    const priority = getPriorityLevel(age, inSchool, hasSupport);
    const config = priorityConfig[priority];
    return (
      <span
        className={`priority-badge ${config.animate ? "pulse" : ""}`}
        style={{ background: `${config.color}20`, color: config.color, border: `1px solid ${config.color}40` }}
      >
        <i className={`bi bi-${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  const StatCard = ({ icon, label, value, color, subtitle, urgent }) => (
    <div className={`stat-card ${urgent ? "urgent" : ""}`} style={{ "--accent": color }}>
      <div className="stat-icon" style={{ background: `${color}15`, color }}>
        <i className={`bi bi-${icon}`}></i>
      </div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {subtitle && <div className="stat-subtitle">{subtitle}</div>}
      </div>
    </div>
  );

  const MobileCard = ({ item }) => {
    const priority = getPriorityLevel(item.imyaka, item.asubiye_mw_ishuri, item.afite_ubufasha);
    const isExpanded = expandedCard === item.id;

    return (
      <div className={`mobile-card ${priority === "critical" ? "critical" : ""}`}>
        <div className="card-header" onClick={() => setExpandedCard(isExpanded ? null : item.id)}>
          <div className="card-main">
            <h4 className="card-name">{item.amazina}</h4>
            <div className="card-meta">
              <span className={parseInt(item.imyaka) < 16 ? "age-danger" : ""}>
                {item.imyaka} imyaka
              </span>
              <span className="separator">•</span>
              <span>Umwana: {item.umwana_ufite_imyaka}</span>
            </div>
          </div>
          <PriorityBadge age={item.imyaka} inSchool={item.asubiye_mw_ishuri} hasSupport={item.afite_ubufasha} />
        </div>

        <div className={`card-body ${isExpanded ? "expanded" : ""}`}>
          <div className="card-info">
            <div className="info-row">
              <i className="bi bi-geo-alt"></i>
              <span>{item.aderesi}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-telephone"></i>
              <a href={`tel:${item.nimero_ya_telephone}`}>{item.nimero_ya_telephone}</a>
            </div>
            <div className="info-row tags">
              <span className={`tag ${item.asubiye_mw_ishuri === "Yego" ? "success" : "muted"}`}>
                {item.asubiye_mw_ishuri === "Yego" ? "Yiga" : "Ntiyiga"}
              </span>
              <span className={`tag ${item.afite_ubufasha === "Yego" ? "success" : "muted"}`}>
                {item.afite_ubufasha === "Yego" ? "Afashijwe" : "Ntiyafashijwe"}
              </span>
            </div>
            {item.icyo_akeneye && (
              <div className="info-row need">
                <i className="bi bi-heart"></i>
                <span>{item.icyo_akeneye}</span>
              </div>
            )}
          </div>

          <div className="card-actions">
            <button className="action-btn success" onClick={() => { setSelectedPerson(item); setShowSupportModal(true); }}>
              <i className="bi bi-heart-fill"></i> Fasha
            </button>
            <button className="action-btn primary" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="action-btn danger" onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div className="card-expand" onClick={() => setExpandedCard(isExpanded ? null : item.id)}>
          <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}></i>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .app-container {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          background: ${theme.bg};
          min-height: 100vh;
          color: ${theme.text};
          -webkit-font-smoothing: antialiased;
        }

        /* Glass Effect */
        .glass {
          background: ${theme.surface};
          backdrop-filter: ${theme.blur};
          -webkit-backdrop-filter: ${theme.blur};
          border: 1px solid ${theme.border};
          border-radius: 20px;
        }

        /* Header */
        .app-header {
          padding: 24px;
          margin-bottom: 24px;
        }

        .header-content {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .header-title-group {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: ${theme.gradient};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
        }

        .header-title {
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 800;
          background: ${theme.gradient};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-subtitle {
          color: ${theme.textMuted};
          font-size: 0.9rem;
          margin-top: 4px;
        }

        .header-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        /* Buttons */
        .btn-glow {
          padding: 12px 20px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.9rem;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-primary {
          background: ${theme.gradient};
          color: white;
          box-shadow: 0 4px 20px ${theme.primaryGlow};
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px ${theme.primaryGlow};
        }

        .btn-ghost {
          background: transparent;
          color: ${theme.text};
          border: 1px solid ${theme.border};
        }

        .btn-ghost:hover {
          background: rgba(255,255,255,0.05);
          border-color: ${theme.primary};
          color: ${theme.primary};
        }

        .btn-success {
          background: ${theme.success};
          color: #000;
        }

        .btn-warning {
          background: ${theme.warning};
          color: #000;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          padding: 20px;
          border-radius: 16px;
          background: ${theme.surface};
          border: 1px solid ${theme.border};
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
          border-color: var(--accent);
        }

        .stat-card.urgent {
          border-color: ${theme.danger};
          animation: urgentPulse 2s infinite;
        }

        @keyframes urgentPulse {
          0%, 100% { box-shadow: 0 0 0 0 ${theme.dangerGlow}; }
          50% { box-shadow: 0 0 0 8px transparent; }
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 800;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.85rem;
          color: ${theme.textMuted};
          margin-top: 4px;
        }

        .stat-subtitle {
          font-size: 0.75rem;
          color: ${theme.textMuted};
          opacity: 0.7;
        }

        /* Filters */
        .filters-bar {
          padding: 16px 20px;
          margin-bottom: 24px;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr auto;
          gap: 12px;
          align-items: center;
        }

        @media (max-width: 992px) {
          .filters-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 576px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        .search-input {
          position: relative;
        }

        .search-input i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: ${theme.textMuted};
        }

        .search-input input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.95rem;
          outline: none;
          transition: all 0.3s;
        }

        .search-input input:focus {
          border-color: ${theme.primary};
          box-shadow: 0 0 0 4px ${theme.primaryGlow};
        }

        .filter-select {
          padding: 14px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.9rem;
          cursor: pointer;
          outline: none;
        }

        .filter-select:focus {
          border-color: ${theme.primary};
        }

        .view-toggle {
          display: flex;
          background: rgba(0,0,0,0.3);
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid ${theme.border};
        }

        .view-toggle button {
          padding: 12px 16px;
          background: transparent;
          border: none;
          color: ${theme.textMuted};
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle button.active {
          background: ${theme.primary};
          color: #000;
        }

        /* Table */
        .data-table {
          overflow: hidden;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .modern-table {
          width: 100%;
          border-collapse: collapse;
        }

        .modern-table th {
          padding: 16px;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${theme.textMuted};
          background: rgba(0,0,0,0.3);
          border-bottom: 1px solid ${theme.border};
          white-space: nowrap;
        }

        .modern-table td {
          padding: 16px;
          font-size: 0.9rem;
          border-bottom: 1px solid ${theme.border};
          vertical-align: middle;
        }

        .modern-table tbody tr {
          transition: background 0.2s;
        }

        .modern-table tbody tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .table-name {
          font-weight: 600;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .table-link {
          color: ${theme.primary};
          text-decoration: none;
        }

        .table-link:hover {
          text-decoration: underline;
        }

        .table-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .table-action {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid ${theme.border};
          background: transparent;
          color: ${theme.textMuted};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .table-action:hover {
          transform: translateY(-2px);
        }

        .table-action.edit:hover {
          border-color: ${theme.primary};
          color: ${theme.primary};
          background: ${theme.primaryGlow};
        }

        .table-action.delete:hover {
          border-color: ${theme.danger};
          color: ${theme.danger};
          background: ${theme.dangerGlow};
        }

        .table-action.support:hover {
          border-color: ${theme.success};
          color: ${theme.success};
          background: ${theme.successGlow};
        }

        /* Priority Badge */
        .priority-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .priority-badge.pulse {
          animation: badgePulse 2s infinite;
        }

        @keyframes badgePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        /* Status Tag */
        .status-tag {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-tag.success {
          background: ${theme.successGlow};
          color: ${theme.success};
        }

        .status-tag.muted {
          background: rgba(255,255,255,0.05);
          color: ${theme.textMuted};
        }

        /* Mobile Cards */
        .cards-container {
          display: none;
        }

        @media (max-width: 992px) {
          .table-container { display: none !important; }
          .cards-container { display: block !important; }
        }

        .mobile-card {
          background: ${theme.surface};
          border: 1px solid ${theme.border};
          border-radius: 16px;
          margin-bottom: 12px;
          overflow: hidden;
          transition: all 0.3s;
        }

        .mobile-card.critical {
          border-color: ${theme.danger};
          border-width: 2px;
        }

        .mobile-card .card-header {
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
        }

        .mobile-card .card-name {
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .mobile-card .card-meta {
          font-size: 0.85rem;
          color: ${theme.textMuted};
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .mobile-card .age-danger {
          color: ${theme.danger};
          font-weight: 600;
        }

        .mobile-card .card-body {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .mobile-card .card-body.expanded {
          max-height: 500px;
        }

        .mobile-card .card-info {
          padding: 0 16px 16px;
        }

        .mobile-card .info-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          font-size: 0.9rem;
          color: ${theme.textMuted};
          border-bottom: 1px solid ${theme.border};
        }

        .mobile-card .info-row:last-child {
          border-bottom: none;
        }

        .mobile-card .info-row i {
          color: ${theme.primary};
          width: 20px;
        }

        .mobile-card .info-row a {
          color: ${theme.primary};
          text-decoration: none;
        }

        .mobile-card .info-row.tags {
          gap: 8px;
          flex-wrap: wrap;
        }

        .mobile-card .tag {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .mobile-card .tag.success {
          background: ${theme.successGlow};
          color: ${theme.success};
        }

        .mobile-card .tag.muted {
          background: rgba(255,255,255,0.05);
          color: ${theme.textMuted};
        }

        .mobile-card .info-row.need {
          background: rgba(255,165,2,0.1);
          border-radius: 8px;
          padding: 10px;
          color: ${theme.warning};
          border: none;
          margin-top: 8px;
        }

        .mobile-card .card-actions {
          display: flex;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid ${theme.border};
        }

        .mobile-card .action-btn {
          flex: 1;
          padding: 10px;
          border-radius: 10px;
          border: none;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .mobile-card .action-btn.success {
          background: ${theme.success};
          color: #000;
        }

        .mobile-card .action-btn.primary {
          background: rgba(255,255,255,0.1);
          color: ${theme.primary};
        }

        .mobile-card .action-btn.danger {
          background: rgba(255,255,255,0.1);
          color: ${theme.danger};
        }

        .mobile-card .card-expand {
          text-align: center;
          padding: 8px;
          color: ${theme.textMuted};
          font-size: 0.8rem;
          cursor: pointer;
        }

        /* Pagination */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid ${theme.border};
          background: transparent;
          color: ${theme.textMuted};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          border-color: ${theme.primary};
          color: ${theme.primary};
        }

        .page-btn.active {
          background: ${theme.gradient};
          color: white;
          border-color: transparent;
        }

        .page-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 4rem;
          color: ${theme.textMuted};
          opacity: 0.3;
          margin-bottom: 16px;
        }

        .empty-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .empty-text {
          color: ${theme.textMuted};
          margin-bottom: 20px;
        }

        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 3px solid ${theme.border};
          border-top-color: ${theme.primary};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Modal Styles */
        .modal-content {
          background: ${theme.surface} !important;
          border: 1px solid ${theme.border} !important;
          border-radius: 20px !important;
          color: ${theme.text} !important;
        }

        .modal-header {
          border-bottom: 1px solid ${theme.border} !important;
          padding: 20px 24px !important;
        }

        .modal-title {
          font-weight: 700 !important;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .modal-body {
          padding: 24px !important;
        }

        .modal-footer {
          border-top: 1px solid ${theme.border} !important;
          padding: 16px 24px !important;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: ${theme.textMuted};
          margin-bottom: 8px;
        }

        .form-input, .form-select {
          width: 100%;
          padding: 14px 16px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.95rem;
          outline: none;
          transition: all 0.3s;
        }

        .form-input:focus, .form-select:focus {
          border-color: ${theme.primary};
          box-shadow: 0 0 0 4px ${theme.primaryGlow};
        }

        .form-input.error {
          border-color: ${theme.danger};
        }

        .form-error {
          color: ${theme.danger};
          font-size: 0.8rem;
          margin-top: 6px;
        }

        .form-input.filled {
          border-left: 3px solid ${theme.success};
        }

        /* Auto-fill Banner */
        .autofill-banner {
          background: linear-gradient(135deg, ${theme.primaryGlow}, rgba(124,58,237,0.1));
          border: 1px solid ${theme.primary}40;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .autofill-text {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .autofill-icon {
          font-size: 1.5rem;
          color: ${theme.warning};
        }

        .autofill-btn {
          padding: 10px 16px;
          background: transparent;
          border: 1px solid ${theme.primary};
          border-radius: 10px;
          color: ${theme.primary};
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s;
          white-space: nowrap;
        }

        .autofill-btn:hover {
          background: ${theme.primaryGlow};
        }

        /* Alert Toast */
        .alert-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideIn 0.4s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-actions {
            width: 100%;
          }

          .header-actions .btn-glow {
            flex: 1;
            justify-content: center;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .stat-card {
            padding: 16px;
          }

          .stat-value {
            font-size: 1.4rem;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .header-actions {
            flex-direction: column;
          }

          .header-actions .btn-glow {
            width: 100%;
          }
        }
      `}</style>

      <div className="app-container">
        <Container fluid className="py-4 px-3 px-md-4">
          
          {/* Alert Toast */}
          {alert.show && (
            <div className="alert-toast">
              <Alert variant={alert.type} onClose={() => setAlert({ show: false })} dismissible className="glass border-0 shadow-lg">
                <i className={`bi bi-${alert.type === "success" ? "check-circle-fill" : "exclamation-triangle-fill"} me-2`}></i>
                {alert.message}
              </Alert>
            </div>
          )}

          {/* Header */}
          <div className="glass app-header">
            <div className="header-content">
              <div className="header-title-group">
                <div className="header-icon">
                  <i className="bi bi-people-fill"></i>
                </div>
                <div>
                  <h1 className="header-title">Abakobwa Babyaye</h1>
                  <p className="header-subtitle">Gucunga no gufasha abakobwa babyaye bakiri bato</p>
                </div>
              </div>

              <div className="header-actions">
                <button className="btn-glow btn-ghost" onClick={loadData} disabled={loading}>
                  <i className="bi bi-arrow-repeat"></i>
                  <span className="d-none d-sm-inline">Refresh</span>
                </button>
                <button className="btn-glow btn-success" onClick={handleInstantSave} disabled={saving}>
                  <i className="bi bi-lightning-fill"></i>
                  <span className="d-none d-sm-inline">Bika 1</span>
                </button>
                <button className="btn-glow btn-warning" onClick={() => handleBatchSave(5)} disabled={saving}>
                  <i className="bi bi-lightning-charge-fill"></i>
                  <span className="d-none d-sm-inline">Bika 5</span>
                </button>
                <button className="btn-glow btn-primary" onClick={openNewForm}>
                  <i className="bi bi-plus-lg"></i>
                  <span>Ongeraho</span>
                </button>
              </div>
            </div>
          </div>

          {/* Urgent Alert */}
          {statistics.needsUrgentSupport > 0 && (
            <Alert variant="danger" className="glass border-danger border-2 mb-4">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Icyitonderwa:</strong> Hari abakobwa <strong>{statistics.needsUrgentSupport}</strong> bakeneye ubufasha bwihuse!
            </Alert>
          )}

          {/* Stats Grid */}
          <div className="stats-grid">
            <StatCard icon="people-fill" label="Abakobwa Bose" value={statistics.total} color={theme.primary} />
            <StatCard icon="mortarboard-fill" label="Bari mu Ishuri" value={statistics.inSchool} color={theme.success} subtitle={`${statistics.total > 0 ? Math.round((statistics.inSchool / statistics.total) * 100) : 0}%`} />
            <StatCard icon="hand-thumbs-up-fill" label="Bafite Ubufasha" value={statistics.withSupport} color={theme.secondary} />
            <StatCard icon="exclamation-triangle-fill" label="Byihutirwa Cyane" value={statistics.needsUrgentSupport} color={theme.danger} urgent={statistics.needsUrgentSupport > 0} />
          </div>

          {/* Filters */}
          <div className="glass filters-bar">
            <div className="filters-grid">
              <div className="search-input">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Shakisha amazina, aderesi, telephone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select className="filter-select" value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}>
                <option value="all">Ishuri Byose</option>
                <option value="Yego">Bari mu ishuri</option>
                <option value="Oya">Batari mu ishuri</option>
              </select>

              <select className="filter-select" value={filterSupport} onChange={(e) => setFilterSupport(e.target.value)}>
                <option value="all">Ubufasha Byose</option>
                <option value="Yego">Bafite ubufasha</option>
                <option value="Oya">Nta bufasha</option>
              </select>

              <select className="filter-select" value={filterAge} onChange={(e) => setFilterAge(e.target.value)}>
                <option value="all">Imyaka Byose</option>
                <option value="under16">Munsi ya 16</option>
                <option value="16-18">16 - 18</option>
                <option value="above18">Hejuru ya 18</option>
              </select>

              <div className="view-toggle d-none d-lg-flex">
                <button className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")}>
                  <i className="bi bi-table"></i>
                </button>
                <button className={viewMode === "card" ? "active" : ""} onClick={() => setViewMode("card")}>
                  <i className="bi bi-grid"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Data Display */}
          {loading ? (
            <div className="glass">
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="mt-3" style={{ color: theme.textMuted }}>Tegereza gato...</p>
              </div>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="glass">
              <div className="empty-state">
                <i className="bi bi-inbox empty-icon"></i>
                <h3 className="empty-title">Nta makuru abonetse</h3>
                <p className="empty-text">Tangira wongeremo amakuru cyangwa uhindure filters.</p>
                <button className="btn-glow btn-primary" onClick={() => handleBatchSave(5)}>
                  <i className="bi bi-lightning-charge-fill me-2"></i>
                  Ongeraho 5 mu buryo bwikora
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="glass data-table table-container" style={{ display: viewMode === "table" ? "block" : "none" }}>
                <div className="table-scroll">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Amazina</th>
                        <th>Imyaka</th>
                        <th>Umwana</th>
                        <th>Telephone</th>
                        <th>Ishuri</th>
                        <th>Ubufasha</th>
                        <th>Icyiciro</th>
                        <th style={{ textAlign: "right" }}>Ibikorwa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                          <td className="table-name">{item.amazina}</td>
                          <td>
                            <span className={parseInt(item.imyaka) < 16 ? "text-danger fw-bold" : ""}>{item.imyaka}</span>
                          </td>
                          <td>{item.umwana_ufite_imyaka}</td>
                          <td>
                            <a href={`tel:${item.nimero_ya_telephone}`} className="table-link">{item.nimero_ya_telephone}</a>
                          </td>
                          <td>
                            <span className={`status-tag ${item.asubiye_mw_ishuri === "Yego" ? "success" : "muted"}`}>
                              {item.asubiye_mw_ishuri}
                            </span>
                          </td>
                          <td>
                            <span className={`status-tag ${item.afite_ubufasha === "Yego" ? "success" : "muted"}`}>
                              {item.afite_ubufasha || "Oya"}
                            </span>
                          </td>
                          <td>
                            <PriorityBadge age={item.imyaka} inSchool={item.asubiye_mw_ishuri} hasSupport={item.afite_ubufasha} />
                          </td>
                          <td>
                            <div className="table-actions">
                              <button className="table-action support" onClick={() => { setSelectedPerson(item); setShowSupportModal(true); }}>
                                <i className="bi bi-heart"></i>
                              </button>
                              <button className="table-action edit" onClick={() => handleEdit(item)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="table-action delete" onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Card View (Desktop when toggled) */}
              {viewMode === "card" && (
                <Row className="g-3 d-none d-lg-flex">
                  {currentItems.map((item) => (
                    <Col lg={4} key={item.id}>
                      <MobileCard item={item} />
                    </Col>
                  ))}
                </Row>
              )}

              {/* Mobile Cards */}
              <div className="cards-container">
                {currentItems.map((item) => (
                  <MobileCard key={item.id} item={item} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={page}
                        className={`page-btn ${page === currentPage ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </Container>

        {/* ═══════════════════════════════════════════════════════════
            MODALS
        ═══════════════════════════════════════════════════════════ */}

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className={`bi bi-${editId ? "pencil-square" : "plus-circle"} me-2`} style={{ color: theme.primary }}></i>
              {editId ? "Hindura Amakuru" : "Ongeraho Umukobwa"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Auto-fill Banner */}
            {!editId && (
              <div className="autofill-banner" key={autoFillKey}>
                <div className="autofill-text">
                  <i className="bi bi-lightning-fill autofill-icon"></i>
                  <div>
                    <strong>Amakuru yuzuzanyije!</strong>
                    <p className="mb-0 small" style={{ color: theme.textMuted }}>Ushobora kuyahindura cyangwa gusubiramo.</p>
                  </div>
                </div>
                <button className="autofill-btn" onClick={regenerateForm}>
                  <i className="bi bi-arrow-repeat"></i>
                  Uzuza Bundi
                </button>
              </div>
            )}

            <Form>
              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Amazina</label>
                    <input
                      type="text"
                      className={`form-input ${form.amazina ? "filled" : ""} ${errors.amazina ? "error" : ""}`}
                      name="amazina"
                      value={form.amazina}
                      onChange={handleChange}
                      placeholder="Amazina yose..."
                    />
                    {errors.amazina && <div className="form-error">{errors.amazina}</div>}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form-group">
                    <label className="form-label">Imyaka</label>
                    <input
                      type="number"
                      className={`form-input ${form.imyaka ? "filled" : ""} ${errors.imyaka ? "error" : ""}`}
                      name="imyaka"
                      value={form.imyaka}
                      onChange={handleChange}
                      placeholder="10-25"
                    />
                    {errors.imyaka && <div className="form-error">{errors.imyaka}</div>}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form-group">
                    <label className="form-label">Telephone</label>
                    <input
                      type="text"
                      className={`form-input ${form.nimero_ya_telephone ? "filled" : ""} ${errors.nimero_ya_telephone ? "error" : ""}`}
                      name="nimero_ya_telephone"
                      value={form.nimero_ya_telephone}
                      onChange={handleChange}
                      placeholder="07XXXXXXXX"
                    />
                    {errors.nimero_ya_telephone && <div className="form-error">{errors.nimero_ya_telephone}</div>}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Aderesi</label>
                    <input
                      type="text"
                      className={`form-input ${form.aderesi ? "filled" : ""} ${errors.aderesi ? "error" : ""}`}
                      name="aderesi"
                      value={form.aderesi}
                      onChange={handleChange}
                      placeholder="Aderesi..."
                    />
                    {errors.aderesi && <div className="form-error">{errors.aderesi}</div>}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Imyaka y'umwana</label>
                    <input
                      type="number"
                      className={`form-input ${form.umwana_ufite_imyaka ? "filled" : ""}`}
                      name="umwana_ufite_imyaka"
                      value={form.umwana_ufite_imyaka}
                      onChange={handleChange}
                      placeholder="Imyaka..."
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Yasubiye mu ishuri?</label>
                    <select className="form-select" name="asubiye_mw_ishuri" value={form.asubiye_mw_ishuri} onChange={handleChange}>
                      <option value="Oya">Oya</option>
                      <option value="Yego">Yego</option>
                    </select>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Afite ubufasha?</label>
                    <select className="form-select" name="afite_ubufasha" value={form.afite_ubufasha} onChange={handleChange}>
                      <option value="Oya">Oya</option>
                      <option value="Yego">Yego</option>
                    </select>
                  </div>
                </Col>
              </Row>

              {form.asubiye_mw_ishuri === "Yego" && (
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label">Ishuri ryiga</label>
                      <input
                        type="text"
                        className={`form-input ${form.ishuri_ryiga ? "filled" : ""}`}
                        name="ishuri_ryiga"
                        value={form.ishuri_ryiga}
                        onChange={handleChange}
                        placeholder="Izina ry'ishuri..."
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label">Umwaka</label>
                      <input
                        type="text"
                        className={`form-input ${form.umwaka_w_amashuri ? "filled" : ""}`}
                        name="umwaka_w_amashuri"
                        value={form.umwaka_w_amashuri}
                        onChange={handleChange}
                        placeholder="S1, S2..."
                      />
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={12}>
                  <div className="form-group">
                    <label className="form-label">Icyo akeneye</label>
                    <input
                      type="text"
                      className={`form-input ${form.icyo_akeneye ? "filled" : ""}`}
                      name="icyo_akeneye"
                      value={form.icyo_akeneye}
                      onChange={handleChange}
                      placeholder="Icyo akeneye cyane..."
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Bireke
            </Button>
            {!editId && (
              <Button variant="outline-warning" onClick={regenerateForm}>
                <i className="bi bi-arrow-repeat me-2"></i>
                Uzuza Bundi
              </Button>
            )}
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : <i className={`bi bi-${editId ? "check-lg" : "save"} me-2`}></i>}
              {editId ? "Hindura" : "Bika"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "3rem", color: theme.danger }}></i>
            </div>
            <h5 className="fw-bold mb-2">Emeza Gusiba</h5>
            <p style={{ color: theme.textMuted }}>Urashaka koko gusiba aya makuru? Ntibizagaruka.</p>
            <div className="d-flex gap-2 justify-content-center mt-4">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Bireke</Button>
              <Button variant="danger" onClick={handleDelete}>
                <i className="bi bi-trash me-2"></i>Siba
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Support Modal */}
        <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-heart-fill me-2" style={{ color: theme.success }}></i>
              Tanga Ubufasha
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPerson && (
              <div className="glass p-3 mb-4" style={{ background: `${theme.success}10`, borderColor: `${theme.success}40` }}>
                <strong>{selectedPerson.amazina}</strong>
                <p className="small mb-0" style={{ color: theme.textMuted }}>
                  {selectedPerson.imyaka} imyaka — {selectedPerson.aderesi}
                </p>
                {selectedPerson.icyo_akeneye && (
                  <p className="small mb-0 mt-2" style={{ color: theme.warning }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Akeneye: {selectedPerson.icyo_akeneye}
                  </p>
                )}
              </div>
            )}
            <Form>
              <div className="form-group">
                <label className="form-label">Ubwoko bw'ubufasha</label>
                <select className="form-select">
                  <option value="">Hitamo...</option>
                  <option value="financial">Amafaranga</option>
                  <option value="education">Uburezi</option>
                  <option value="health">Ubuzima</option>
                  <option value="counseling">Ubujyanama</option>
                  <option value="vocational">Amahugurwa</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Ibisobanuro</label>
                <textarea
                  className="form-input"
                  rows="3"
                  placeholder="Sobanura ubufasha..."
                ></textarea>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSupportModal(false)}>Bireke</Button>
            <Button variant="success" onClick={() => { showAlert(`Ubufasha bwa "${selectedPerson?.amazina}" bwanditswe!`, "success"); setShowSupportModal(false); }}>
              <i className="bi bi-check-lg me-2"></i>Bika Ubufasha
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default AbakobwaBabyaye;